import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RichTextField } from "./RichTextField";
import { toHtml } from "../lib/richtext";

const Ctx = createContext(null);

export function FieldCommentsProvider({ page, clientId, children }) {
  const [list, setList] = useState([]);
  const base = clientId ? `/admin/clients/${clientId}/comments/${page}` : `/client/comments/${page}`;
  const viewer = clientId ? "admin" : "cliente";
  useEffect(() => { api.get(base).then(({ data }) => setList(data || [])).catch(() => {}); }, [base]);
  const add = (c) => {
    setList((l) => [...l, { ...c, validated: true, author: viewer, author_name: viewer === "admin" ? "Fanny" : "Moi", created_at: new Date().toISOString() }]);
    api.post(base, c).catch(() => toast.error("Commentaire non enregistré, réessaie."));
  };
  const remove = (id) => { setList((l) => l.filter((x) => x.id !== id)); api.delete(`${base}/${id}`).catch(() => {}); };
  return <Ctx.Provider value={{ list, add, remove, viewer }}>{children}</Ctx.Provider>;
}

function CommentItem({ c, viewer, onRemove }) {
  const coach = (c.author || "admin") === "admin";
  const mine = (c.author || "admin") === viewer;
  const who = coach ? (viewer === "admin" ? "Moi" : "Fanny, ta coach") : (viewer === "cliente" ? "Moi" : c.author_name && c.author_name !== "Moi" ? c.author_name : "Ta cliente");
  return (
    <div data-testid={`field-comment-${c.id}`} className="flex gap-2.5 rounded-2xl bg-[#2D181E] p-3 text-white shadow-[0_8px_24px_rgba(45,24,30,0.18)]">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${coach ? "bg-[#F3D2D9] text-[#590224]" : "bg-bordeaux text-white"}`}>{coach ? "F" : (c.author_name || "M").charAt(0).toUpperCase()}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold">{who}</span>
          {mine && <button type="button" data-testid={`field-comment-delete-${c.id}`} onClick={() => onRemove(c.id)} title="Supprimer" className="text-white/40 hover:text-white"><X size={13} /></button>}
        </div>
        {c.quote && <p className="mt-1 line-clamp-2 border-l-2 border-[#F3D2D9] pl-2 text-[11px] italic text-white/60">« {c.quote} »</p>}
        <p className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-white/90">{c.text}</p>
      </div>
    </div>
  );
}

export function CommentRail({ wrapRef, comments, viewer, onRemove, children, testid }) {
  const [pos, setPos] = useState({ tops: {}, mode: "stack", w: 300, minH: 0 });
  const has = comments.length > 0;
  const sig = comments.map((c) => c.id).join(",");
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!has || !el) return undefined;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const room = document.documentElement.clientWidth - box.right - 24;
      const mode = window.innerWidth < 1024 ? "stack" : room >= 240 ? "out" : box.width >= 760 ? "in" : "stack";
      const w = mode === "out" ? Math.min(300, room) : 300;
      const raw = comments.map((c) => {
        const s = el.querySelector(`[data-cid="${c.id}"]`) || (c.key && el.querySelector(`[data-field="${c.key}"]`));
        return { id: c.id, top: s ? s.getBoundingClientRect().top - box.top : 0 };
      }).sort((a, b) => a.top - b.top);
      let last = -Infinity;
      const tops = {};
      raw.forEach((r) => {
        const t = Math.max(r.top, last);
        tops[r.id] = t;
        last = t + (el.querySelector(`[data-rail-id="${r.id}"]`)?.offsetHeight || 110) + 10;
      });
      const nxt = { tops, mode, w, minH: last };
      setPos((p) => (JSON.stringify(p) === JSON.stringify(nxt) ? p : nxt));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [sig, has]); // eslint-disable-line react-hooks/exhaustive-deps
  const side = has && pos.mode !== "stack";
  const out = has && pos.mode === "out";
  return (
    <div ref={wrapRef} className={`relative ${side && !out ? "pr-[316px]" : ""}`} style={side ? { minHeight: pos.minH } : undefined}>
      {children}
      {has && (
        <div data-testid={testid ? `${testid}-comments` : undefined} data-mode={has ? pos.mode : undefined} className={side ? "" : "mt-2 space-y-2"}>
          {comments.map((c) => (
            <div key={c.id} data-rail-id={c.id} className={side ? "absolute z-20 transition-[top] duration-200" : ""} style={side ? { top: pos.tops[c.id] ?? 0, width: pos.w, ...(out ? { left: "calc(100% + 16px)" } : { right: 0 }) } : undefined}>
              <CommentItem c={c} viewer={viewer} onRemove={onRemove} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentField({ fieldKey, value, onChange, onBlur, readOnly = false, placeholder, className, dark, testid, empty }) {
  const ctx = useContext(Ctx);
  const wrap = useRef(null);
  const comments = ctx ? ctx.list.filter((c) => c.key === fieldKey && (c.text || "").trim() && (readOnly || (c.author || "admin") === "admin" || (value || "").includes(`data-cid="${c.id}"`))) : [];
  const remove = (id) => {
    ctx.remove(id);
    const span = wrap.current?.querySelector(`[data-cid="${id}"]`);
    if (!span) return;
    const p = span.parentNode;
    while (span.firstChild) p.insertBefore(span.firstChild, span);
    p.removeChild(span);
    const field = wrap.current.querySelector("[data-field]");
    if (!readOnly && field) { onChange?.(field.innerHTML); onBlur?.(field.innerHTML); }
  };
  const onComment = ctx ? (c) => { ctx.add(c); if (!readOnly) onBlur?.(wrap.current.querySelector("[data-field]").innerHTML); } : undefined;
  const showEmpty = readOnly && !value && empty;
  return (
    <CommentRail wrapRef={wrap} comments={comments} viewer={ctx?.viewer} onRemove={remove} testid={testid}>
      {showEmpty ? <div className={className}>{empty}</div> : (
        <RichTextField key={readOnly ? toHtml(value || "") : fieldKey} testid={testid} fieldKey={fieldKey} initialHtml={value || ""} readOnly={readOnly}
          onChange={onChange} onBlur={onBlur} onComment={onComment} placeholder={placeholder} dark={dark} className={className} />
      )}
    </CommentRail>
  );
}
